/**
 * The passenger band, correctable in place on the Quote screen (T372), and
 * beside it the REAL headcount (T651 M7, R21).
 *
 * ⛔ WHY THE COUNT LIVES IN THIS FILE AND NOT ITS OWN. The two are one fact
 * split in two: `passengers_band` is the vehicle-capacity TIER the price is
 * keyed on, `passengers_exact` is how many people are actually travelling, and
 * `bookings.passengers` between them is neither (v17: it "looks like a count
 * but is derived by bandToPax() from the band's CEILING, so a party of 5 and a
 * party of 8 both persist as 8"). Rendering the count anywhere but touching
 * distance from the chips invites Helen to read one as the other.
 *
 * ⛔ THE COUNT IS OPTIONAL. It is not a second required field on a screen whose
 * whole design constraint is "not too many buttons": blank is the default and
 * blank persists as NULL ("history genuinely does not know it - a fabricated
 * backfill would be worse than an honest NULL").
 *
 * ⛔⛔ AMENDED, T1871 M5 (board #1871) — IT MOVES MONEY NOW. This block used to
 * end "and MOVES NO MONEY ... nothing re-prices when it changes", and both
 * halves are now FALSE: `setPassengersExact` (client/lib/actions.ts) derives the
 * band from the count and writes it through `setBand`, which re-prices. A
 * shipped comment forbidding what the shipped code does is worse than no
 * comment, so it is corrected here rather than left to be believed.
 * ⭐ WHAT SURVIVES THE AMENDMENT, because it is the part that was never about
 * re-pricing: the count is still OPTIONAL, still blank by default, still NULL
 * for "not captured", and it is still never a `/api/quote` field. It moves money
 * only by moving the BAND, which is the pricing key.
 *
 * The router lands a lead with a valid band straight on Quote — skipping the
 * Passengers screen — and `goBack` only retraces screens Helen ACTUALLY visited,
 * so a band she never got asked about had no route back at all. "Actually we're
 * 8-12" was unfixable without starting the job again. This is that route.
 *
 * It is a SIBLING of `Requirements`, never a child: `manual-quote.spec.tsx`
 * renders `<Requirements>` directly and asserts exactly ONE 'Edited' badge, so a
 * band badge nested inside it would break a green spec.
 *
 * Reuses the shipped quiet single-select (the Bookings status filter's chip-row)
 * and the band a11y contract (aria-pressed, as on Passengers — it is what the
 * e2e net grades against). Zero new CSS.
 */
import { useState } from 'preact/hooks';
// ⛔ THE ENGINE'S OWN OVER-BAND TOKEN. `check-copy.sh`'s enumeration rule bans
// the four PRICED band literals in this region but not this one; its T1871 M6
// `scan_over_band` arm covers this one, across all of src. Import it, exactly as
// PortalRequests.tsx imports BANDS. (This file is where the M6 probe was planted
// to prove that arm reds - the positive control for the gate.)
import { OVER_BAND, bandForCount } from '../../engine/bands.js';
import { bands, type Registry } from '../store/registry.js';
// ⛔ STRAIGHT FROM src/shared, and it is the SERVER's own constant - the one
// readPassengersExact refuses against - not a second copy of 1000. A client
// bound that disagrees with the server is worse than no bound: the box would
// accept a value the quote door then 400s, costing Helen the save of a price
// already agreed on the phone. See the constant's header.
import { MAX_PASSENGERS_EXACT } from '../../shared/passengers.js';
import { Banner } from './Banner.js';

export type BandControlProps = {
  registry: Registry;
  /** The band being priced right now. */
  band: string | null;
  /** The band the CUSTOMER stated — the immutable Edited baseline. null = walk-in. */
  customerBand: string | null;
  onPick: (band: string) => void;
  /** The REAL headcount, or null for not stated. Read for its VALUE by the
   *  over-band state below (never for its presence) as well as rendered. */
  passengersExact: number | null;
  /**
   * ⛔⛔ AMENDED, T1871 M5. This said "Plain patch — MUST NOT re-price (the band
   * is the pricing key; pairing this with fetchQuote would make a dispatch note
   * move the customer's money)", and it now forbids exactly what its own handler
   * does: App.tsx wires this to `setPassengersExact`, and board #1871 made the
   * count the thing Helen actually knows ("there are 17 of us"), so it stopped
   * being a dispatch note.
   *
   * ⛔⛔ AMENDED AGAIN, T1871 M7 (MERGE BLOCKER), AND THE M5 WORDING IS THE PART
   * THAT WAS WRONG. It said this handler "derives the band and re-prices through
   * `setBand`" — ON EVERY KEYSTROKE, which is the measured defect: the one-digit
   * prefix of a two-digit headcount derived a priced band and destroyed the
   * operator's agreed hand price. IT DOES NOT DERIVE ANYTHING ANY MORE. This
   * writes the COUNT and nothing else; `onCommitPassengersExact` below carries
   * the derivation, on a settled value.
   *
   * ⭐ THE INVARIANT THAT REPLACED THE ORIGINAL, and the reason the old one
   * existed: the count still never reaches `/api/quote`. It moves money ONLY by
   * moving the band, and that derivation goes through the paired writer rather
   * than a second patch — a patch would leave a new band beside the previous
   * band's price, the stale pair the pairing rule exists to prevent. The three
   * guards on that transition (null, the operator pin, a BAND delta rather than
   * a changed count) live with it in `client/lib/actions.ts`, where they are
   * enforceable; do not re-implement any of them here.
   */
  onSetPassengersExact: (value: number | null) => void;
  /**
   * ⛔⛔ T1871 M7 (MERGE BLOCKER) — THE COMMIT EDGE, AND IT IS A DIFFERENT
   * RESPONSIBILITY FROM THE ONE ABOVE. `onSetPassengersExact` fires on every
   * keystroke and writes the COUNT; this fires once the value has SETTLED and is
   * what derives the band. Splitting them is the fix for a measured money
   * defect: a two-digit headcount is typed as a one-digit count first, and while
   * the derivation ran on input that transient prefix derived a priced band and
   * destroyed the operator's agreed hand price, which the second digit could not
   * undo. The account, the reproduction and the reason this is an edge rather
   * than a debounce are in `client/lib/actions.ts` on the two writers.
   *
   * ⛔ `blur`, and only blur, because it fires BEFORE the click that caused it:
   * a band chip or an Accept press always lands AFTER the commit, so Helen's own
   * act can never be overwritten by a deferred derivation. A timer can be, which
   * is the trap `setPassengersExact`'s docblock has always warned about.
   *
   * ⚠️ OPTIONAL BECAUSE THE PROP CHAIN IS, NOT BECAUSE THE COMMIT IS. Making it
   * required would force a stub into ~15 spec files that build QuoteScreen
   * props, two of which are being edited concurrently. The wiring it would
   * otherwise leave unpinned is graded directly in over-band-hand-price.spec.tsx
   * ("the wiring is real"), so an unwired prop reds rather than going quietly
   * inert.
   */
  onCommitPassengersExact?: () => void;
};

/** The inline error's id, so the input can name it in `aria-describedby`.
 *  Module-scope: this control renders once per screen (RateList.tsx's
 *  STRIP_ERROR_ID is the in-repo precedent for the whole pattern). */
const EXACT_ERROR_ID = 'passengers-exact-error';

/**
 * The one refusal sentence, EXPORTED so the pin asserts the real string rather
 * than its own copy of it, and so the bound in the words is the bound the
 * server enforces.
 *
 * ⛔ It names the BLANK option because blank is legal here and Helen has to know
 * she is allowed to leave it: an error that only says "1 to 1000" reads as "this
 * field is required", which it is not.
 * ⛔ 'Passengers must be...' and not 'Passengers' alone: e2e's
 * lead-prefill-carry.spec.ts asserts getByText('Passengers', { exact: true }) on
 * this screen, and exact:true does not match a longer sentence. getByLabel
 * matches by SUBSTRING but this is not a label.
 */
export const PASSENGERS_EXACT_ERROR =
  `Passengers must be a whole number from 1 to ${MAX_PASSENGERS_EXACT}, or left blank.`;

export function BandControl({
  registry, band, customerBand, onPick, passengersExact, onSetPassengersExact,
  onCommitPassengersExact,
}: BandControlProps) {
  /**
   * ⭐ WHAT THE OPERATOR TYPED, kept separately from the number that survived
   * parsing (T651 M7 webapp-testing, finding 2 - MEASURED on the served kiosk).
   *
   * THE DEFECT: the input is CONTROLLED off `passengersExact`, and every
   * rejected keystroke set it to null - so `value` snapped back to '' and the
   * box emptied itself under the operator. Typing `0`, a lone `-` or `2.5`
   * produced a blank field, no error text, no red border and no aria-invalid:
   * Helen could not tell a REJECTED value from a keystroke the kiosk never
   * received. (Same class as the blanking bug VenueControl.tsx records, and the
   * same class as the M6 BLOCK where a refusal rendered where nobody could see
   * it.)
   *
   * `null` here means "not editing - show the prop", so an externally-changed
   * headcount (re-open, a fresh job) still displays: the raw string shadows the
   * prop only while there is typing to preserve, and clearing the box hands
   * control straight back.
   */
  const [raw, setRaw] = useState<string | null>(null);
  const [exactError, setExactError] = useState<string | null>(null);
  const known = bands(registry);
  /**
   * ⛔⛔ T1871 M5 — OVER-BAND IS A STATE, NOT A NAG, AND THE TWO SPLIT HERE.
   *
   * Both used to render the same sentence: "Choose a passenger band to price
   * this journey." For a blank band that is true and actionable. For an
   * over-band job it was a LIE that cost Helen the booking — there is no band to
   * choose, every one of the four understates the party, and the correct outcome
   * is a hand price, which `Quote.tsx` now unlocks off exactly this fact.
   *
   * Validity, never truthiness, on BOTH arms. A band that is merely UNKNOWN
   * ('lots' from a hand-edited row) is still a nag case: something is wrong and
   * picking a real band fixes it. Only the engine's own over-band token gets the
   * state.
   */
  /**
   * ⛔⛔ T1871 M5 (BLOCK) — P0-3'S TRIGGER IS A DISJUNCTION AND M5 SHIPPED HALF
   * OF IT. "a count above 16 OR a `16+` band from an older inquiry". Keyed on
   * the band alone, a 22-passenger job with `12-16` tapped rendered a fully
   * computed grid price with Accept ready and no warning at all — the reported
   * defect ("bookings arrive as 12-16 with far more people") intact on the
   * screen built to remove it.
   *
   * ⛔⛔ AMENDED, T1961 (board #1961) — THE DISJUNCTION IS SPLIT INTO ITS TWO
   * STATES BELOW, because they no longer have the same answer. Alex, on
   * production after M6 shipped: "should we just add the 16+ band? this way we
   * dont have to show this message?" Captain ruled option 1 on durable question
   * 835 - add the chip, and NARROW the message rather than delete it.
   *   A. `isOverBand` - the job IS over-band. A pressed fifth chip states that
   *      exactly, in the band row, in the fold. No banner: the lit chip says it.
   *   B. `bandUndercountsParty` - a PRICED band is pinned while the count
   *      derives over-band (Helen's deliberate `12-16` for a party of 22, two
   *      minibuses). That is a band/count DISAGREEMENT and not an over-band job:
   *      no chip may light for it, ask 805 refuses it at the save door and ask
   *      824 at the compose door, and it keeps a warning naming the remedy those
   *      doors name.
   * ⛔ IT IS DELIBERATELY NARROWER THAN THE SERVER'S `passengersBandDisagrees`,
   * and keyed on the over-band DERIVATION rather than on a general band/count
   * inequality. `setPassengersExact` writes on every KEYSTROKE by contract, so a
   * general predicate would fire on every intermediate value - the keystroke
   * class that already destroyed an agreed hand price once (T1871 M7). A save
   * door sees only a settled request; this sees every digit.
   *
   * ⛔ THE VALUE, NEVER THE PRESENCE, and never a bare `> 16`: `null` falls
   * back to the band exactly as before (the standing Captain delta), and the
   * threshold stays in the engine's table where `bands.spec.ts` grades it.
   * `!= null`, never truthiness: `Number(null) === 0` and `bandForCount(0)` is
   * null, so a truthy test would read "not captured" as a party of nobody.
   *
   * ⚠️ IT MAKES THE MISMATCH VISIBLE AND STOPS THERE, and that still holds -
   * but not for the reason written here before. `Quote.tsx`'s `handPriced` is
   * deliberately NOT widened to this: a party of 22 on a `12-16` price may be
   * two minibuses. The old sentence said "refusing that pairing is a product
   * question with Alex, not a client-side decision"; board ask 805 has since
   * answered it - "Refuse disagreement at the save door" - so the refusal now
   * EXISTS, as a 400 at the three save doors in server/app.ts. It is still not a
   * client-side decision, and nothing here blocks a price: the ruling refuses at
   * the door, never by disabling a control, so every tile stays pressable.
   */
  const isOverBand = band === OVER_BAND;
  const pricedBandPicked = band !== null && known.includes(band);
  const bandUndercountsParty = pricedBandPicked
    && passengersExact != null && bandForCount(passengersExact) === OVER_BAND;
  /**
   * ⛔ MUTUAL EXCLUSION BY CONSTRUCTION, not by inspection. `needsChoosing`
   * requires `!pricedBandPicked` and `bandUndercountsParty` requires it, so the
   * nag and the banner can never render together. Left as the old
   * `band === null || !known.includes(band)`, an UNKNOWN band carrying an
   * over-band count ('lots' + 22, which `sourceFromBooking` can restore without
   * passing a commit) would render the nag AND a banner reading "bigger than the
   * band picked" when no band is picked at all.
   * ⚠️ TWO DECLARED BEHAVIOUR CHANGES, both corrections, both pinned in
   * over-band-hand-price.spec.tsx: a NULL band with an over-band count now nags
   * where the old disjunction suppressed it, and so does 'lots' + 22. The nag
   * was a lie on those states only because there was no over-band control to
   * obey it with - the chip above is that control.
   */
  const needsChoosing = !isOverBand && !pricedBandPicked;
  const edited = customerBand !== null && band !== customerBand;

  return (
    <div class="requirements">
      {/* ⭐ `Passenger band`, and R7 GAVE IT A POSITIVE REASON (T1749 M8). The
          heading was chosen as NOT the exact string 'Passengers' because the
          customer card once rendered that as a <dt> and a second exact match is a
          Playwright strict-mode violation (lead-prefill-carry). That collision is
          gone — R7 relabelled the customer card's row `Customer said` — but the
          heading STAYS `Passenger band`, now because these chips are the ONE place
          the WORKING band renders (R7 dropped it from the journey summary), and
          the heading names whose band it sets: Helen's working tier, not the
          customer's stated one on the card. */}
      <h2 class="field-heading">Passenger band</h2>
      {/* ⭐⭐ T1749 M8 (R7 FOLD, measured on the seat) — THE MISMATCH IS STATED
          ABOVE THE CHIPS, NOT BETWEEN THE CHIPS AND THE COUNT BOX.
          ⛔ IT IS A FOLD FIX, AND THE ARITHMETIC IS THE REASON. Under the chip
          row the banner's bottom measured 712 at 1024x700 against R7's ~700 plane
          (`e2e/tests/green/tablet-ia.spec.ts` and the bug-g twin). Typing the
          count FOCUSES the box, so the browser scrolls the pane to reveal it —
          and it computes that scroll from the layout BEFORE the banner exists.
          Everything above the banner therefore moves the banner and the scroll by
          the SAME amount and cancels out: measured on the seat, the pane's top
          padding, the summary card's rhythm and the band row's own spacing all
          net to zero. The only quantities that move the banner relative to the
          viewport are its own occupied height (112px: an 88px two-line box
          between a collapsed 16 and a 12) and the distance from it down to the
          count box (102px) — so spacing alone could win at most 8px of the 12
          needed, and only by cramping a warning against the control it names.
          ⇒ the banner moves UP by the chip row's own height (44 + a collapsed 16),
          which is 60px of headroom rather than 8, and it costs no CSS, no token
          and no copy — the sentence is byte-identical to the server's
          `PASSENGERS_MISMATCH_SAVE_REMEDY` and may not be reworded.
          ⭐ IT ALSO READS BETTER: the disagreement is stated where the band is
          chosen, above the control it asks you to correct, instead of sitting
          between the two controls it names.
          ⚠️ MEASURED, not reasoned: 712 -> 652 on the seat (1024x700, the R7 fold
          probe's own state). Re-derive with
          `npx playwright test tests/green/tablet-ia.spec.ts --project=rate-limited -g "fold"`. */}
      {/* ⛔ ZERO CONTROLS AND ZERO NEW CSS, AND THE REASON IS NOW A CHOICE.
          `Banner` is a `<div role="status">`, which matches none of
          control-budget.spec.tsx's selectors. That used to be a NECESSITY: this
          note read "Quote is pinned at EXACTLY 21 controls, failing in either
          direction, so the headroom here is zero and this had to be a
          non-interactive affordance". T1961 is what SPENT that headroom - the
          fifth chip above IS the 22nd control, and the DEBT row was moved to 22
          with board 1961 recorded as the justification its preamble demands. So
          this stays non-interactive because there is nothing to press about a
          band and a count disagreeing, not because a budget forbade a button.
          ⛔ IT IS STATE B ONLY, and that is the whole T1961 narrowing. A job
          that IS over-band now says so with a LIT CHIP in the band row, which is
          in the fold; a sentence repeating it would be the second voice the nag
          above already carries a warning about - two messages opening on the
          same words read as one stuttering message. What is left here is the one
          state no chip can state: a PRICED band pinned against a count that has
          outgrown it, which is a DISAGREEMENT and so names its remedy.
          ⛔ THE REMEDY HALF IS BYTE-IDENTICAL TO THE SERVER'S
          `PASSENGERS_MISMATCH_SAVE_REMEDY` (server/app.ts), deliberately: ask
          805 refuses this pairing at the save door and ask 824 at the compose
          door, and a screen phrasing the fix differently from the refusal would
          read as a second, unrelated problem. A grep pins the identity.
          ⚠️ THE COPY IS PROVISIONAL - Vicky's QA sitting with the 1749 batch.
          ⚠️ ASCII hyphen, never an em dash; no band literal and no euro figure.
          check-copy.sh scans this file and this string is RENDERED copy, not a
          comment, so nothing here is stripped before the gate reads it. */}
      {bandUndercountsParty && (
        <Banner kind="warn">This party is bigger than the band picked. Correct the band or the number of passengers, then save again.</Banner>
      )}
      <div class="chip-row" role="group" aria-label="Passenger band">
        {known.map((b) => {
          const selected = b === band;
          return (
            <button
              key={b}
              type="button"
              class={`btn btn--quiet chip-btn${selected ? ' chip-btn--active' : ''}`}
              aria-pressed={selected}
              // Deliberately NOT guarded on `b === band`. After a failed
              // re-price the action row is gone and re-picking the ACTIVE band
              // is the only way back to a price — a no-op here would make the
              // error state a dead end that tells Helen to retry and gives her
              // nothing to retry with.
              onClick={() => onPick(b)}
            >
              {b}
            </button>
          );
        })}
        {/* ⛔⛔ THE FIFTH CHIP, AND IT IS NOT A REGISTRY BAND. Why it exists at
            all is the docblock above (T1961, board #1961, ask 835); what follows
            is only what this SITE has to get right.
            ⛔ RENDERED BY HAND, beside `known.map`, and it must never move into
            `bands(registry)` or `/api/options`: those serve the four PRICED
            bands, there is no rate row for an over-band job, and a fifth entry
            there would offer a price that cannot exist. e2e's
            site-form-parity.spec.ts asserts the site's list is the registry bands
            SPREAD with the token appended AFTER them - beside it, never inside.
            ⛔ `{OVER_BAND}`, NEVER THE LITERAL. check-copy.sh's `scan_over_band`
            arm reds on a hand-spelled token anywhere under src but its owner.
            ⛔ AFTER the four and BEFORE `Edited`: the five band chips stay
            contiguous and the Edited marker stays last, so it reads as a badge on
            the row rather than as a sixth band.
            ⛔ IT PINS ON PRESS, with no contradiction gate - App.tsx wires
            `onPick` to `setBand(store, band, true)`. Deliberate, and the
            difference from the Passengers screen (which gates its forward edge on
            `countFitsAPricedBand`, on a security-review measurement) is that the
            count box sits INCHES away here: ask 804's guard re-derives through the
            pin on the next commit, and ask 805 refuses a disagreeing save at the
            door. Zero new CSS - `.chip-row` is `display: flex; flex-wrap: wrap`. */}
        <button
          type="button"
          class={`btn btn--quiet chip-btn${isOverBand ? ' chip-btn--active' : ''}`}
          aria-pressed={isOverBand}
          // Unguarded on the active state, as the four above are and for the same
          // reason (see there).
          onClick={() => onPick(OVER_BAND)}
        >
          {OVER_BAND}
        </button>
        {edited && <span class="chip chip--edited">Edited</span>}
      </div>
      {/* Deliberately worded apart from the failed-re-price banner ("Pick the
          passenger band again to retry"): an invalid band on a job that also
          failed to price renders BOTH, and two sentences opening identically
          read as one stuttering message. */}
      {needsChoosing && (
        <p class="helper-note">Choose a passenger band to price this journey.</p>
      )}

      {/* ⛔ 'Passengers (exact)', NEVER the bare word 'Passengers'.
          At T1749 M8 (R7) the customer card's <dt> stopped being the bare word
          'Passengers' (it now reads `Customer said`), so the exact-'Passengers'
          strict-mode collision `lead-prefill-carry` once tripped on is gone. The
          label STAYS 'Passengers (exact)' anyway: `getByLabel` matches by
          SUBSTRING, so a bare 'Passengers' here would still be poisoned by the
          site form's `getByLabel('Passengers')` (site-submit / site-form-parity)
          the day a kiosk equivalent shared a page with it. The qualifier keeps
          this control distinguishable by label whatever else the screen carries. */}
      {/* ⛔⛔ THIS FIELD DOES NOT ADOPT `FormField`, AND THE REFUSAL IS THE FINDING
          (T1749 M8; the item asked for five fields and four adopted).
          ⛔ THE DECISIVE LIMB IS THE COMMIT EDGE: this box commits on `onBlur`,
          and `FormField` has no `onBlur` prop. The commit is what writes the
          settled count — a refused keystroke commits nothing — so adopting would
          either drop the edge or move it onto a keystroke, and that is input
          validation at a trust boundary, the one class the minimal-code rule
          forbids simplifying away.
          ⚠️ AND THE OTHER LIMB THIS NOTE CARRIED WAS WRONG (round 3, the
          plan-alignment A3 finding). It said `el.validity.badInput` could not be
          read because `onInput` hands over a string with no element passthrough —
          but `FormField` HAS `elementRef`, wired to the input, so the native flag
          that tells a cleared box from one holding text the DOM will not convert
          (a lone '-', 'abc') would have been reachable through the ref. One
          reason, not two.
          ⚠️ `min`/`max` DID reach the library for this field in round 2 and were
          WITHDRAWN in round 3 with no call site (impeccable C3): they come back
          with the field that needs them. */}
      <label class="field-label" for="passengers-exact">Passengers (exact)</label>
      {/* ⛔ THE ERROR RENDERS ABOVE THE INPUT AND IS PLAIN TEXT, because the
          operator this kiosk is built for is SIGHTED and the aria wiring below
          alone would repeat the M6 BLOCK (a refusal only a screen reader heard).
          Banner kind="error" is role="alert", the house pattern, and zero new
          CSS - RateList.tsx pairs the same Banner + aria-invalid +
          aria-describedby trio for its blank-reason refusal.
          ⚠️ NO focus/scroll handling here, deliberately: this fires WHILE Helen
          is typing in the very field it describes, so the caret is already on
          it. The banners that steal focus are the ones answering a button. */}
      {exactError && <Banner kind="error" id={EXACT_ERROR_ID}>{exactError}</Banner>}
      <input
        id="passengers-exact"
        class="login-input"
        type="number"
        inputMode="numeric"
        min="1"
        // ⛔ THE SERVER'S OWN CONSTANT. Before this the field had NO ceiling at
        // all: 999999 was accepted client-side and refused only by the quote
        // door's 400 - which lands in a validation region measured off-screen on
        // this screen. The sibling Wheelchair spaces box has capped since the M7
        // security review; this one was the inconsistent field.
        max={MAX_PASSENGERS_EXACT}
        aria-invalid={exactError ? 'true' : undefined}
        aria-describedby={exactError ? EXACT_ERROR_ID : undefined}
        // Blank, not '0'. The field's null means "not stated" and the column's
        // CHECK refuses 0 outright, so a zero placeholder would be an invalid
        // default sitting in the box.
        // `raw` shadows only while there is unparsed typing to preserve.
        value={raw ?? (passengersExact === null ? '' : String(passengersExact))}
        // ⛔ THE CLIENT-SIDE HALF OF THE > 0 GUARD. Anything that is not a whole
        // number in range becomes null (not stated) rather than being sent: the
        // server refuses it with a 400 on both doors, and on the quote door that
        // 400 would cost Helen the save of a price she has already agreed on the
        // phone. The refusal is the boundary; this is what keeps her from ever
        // meeting it - and now it SAYS SO instead of blanking the box.
        onInput={(e) => {
          const el = e.target as HTMLInputElement;
          const typed = el.value.trim();
          setRaw(typed);
          const n = Number(typed);
          if (typed === '') {
            // ⛔ '' IS TWO DIFFERENT FACTS ON A type="number" BOX, and only the
            // platform can tell them apart. A control holding text it cannot
            // convert ('-' on its own, 'abc') reports `value === ''` exactly
            // like a cleared box - that is why the measured "typing a lone -
            // does nothing visible" had no `value` to react to. `badInput` is
            // the native flag for it (ValidityState), so it is asked rather
            // than a parser being written for text the DOM will not hand over.
            // ⚠️ jsdom never sets badInput, so the unit lane always takes the
            // cleared arm; that limit is stated in the spec rather than faked.
            if (el.validity.badInput) {
              setExactError(PASSENGERS_EXACT_ERROR);
              onSetPassengersExact(null);
              return;
            }
            // Cleared is NOT an error - blank is the field's own "not stated".
            // It also hands display control back to the prop.
            setRaw(null);
            setExactError(null);
            onSetPassengersExact(null);
            return;
          }
          if (Number.isSafeInteger(n) && n > 0 && n <= MAX_PASSENGERS_EXACT) {
            setExactError(null);
            onSetPassengersExact(n);
            return;
          }
          // ⛔ NOTHING INVALID GOES ON THE WIRE. The number is still withheld;
          // what changed is that the operator is told, and keeps her keystrokes.
          setExactError(PASSENGERS_EXACT_ERROR);
          onSetPassengersExact(null);
        }}
        // ⛔ THE COMMIT EDGE. Nothing is parsed again here: the handler above has
        // already written the settled count (or a null for a value it refused),
        // so the commit reads the store rather than the box and a refused
        // keystroke commits nothing. See the prop's docblock for why blur.
        onBlur={() => onCommitPassengersExact?.()}
      />
    </div>
  );
}
